import {
  CRITICAL_ROWS,
  CURRENT_WEEK,
  QUEUED_COHORTS,
  THIN_ROWS,
  TRAINING_WEEKS,
  personHref,
} from '@/demo/content/skillsMatrix'
import { OFFICES, personById } from '@/demo/data'

/**
 * Surface 1.7 — Talent pipeline. Every figure, label, route and sentence the
 * page renders resolves through this module, `@/demo/data`, or the skills
 * matrix content it is downstream of; the component carries no literal.
 *
 * WHAT THE DECK GIVES (product/px-platform-mockups.md §1.7): the funnel
 * (340 → 96 → 11 → 4 → 8), time to billable (11 weeks, was 26 before the PX
 * curriculum), 12-month retention (88%), the four-year headcount plan
 * (8 → 13 → 20 → 35 FTE) and the gating sentence. Those are transcribed as
 * given in DECK below and never re-typed anywhere else.
 *
 * WHAT IS AUTHORED HERE (once): the three cohorts running, the billable-share
 * column on the plan, and the titles the open roles carry. Everything else is
 * derived, so the page cannot contradict itself or the matrix:
 *  - the funnel's "In training" count IS the sum of the running cohorts (guarded);
 *  - the plan's first year IS the funnel's billable count (guarded);
 *  - "11 weeks to billable" IS assessment + training + shadowing (guarded);
 *  - the Assessed → Billable conversion is computed from the funnel;
 *  - every cohort's start week and billable week are computed from CURRENT_WEEK
 *    and the matrix's own TRAINING_WEEKS;
 *  - the open roles ARE the matrix's Critical rows plus its hungriest Thin row.
 *
 * ONE-WORLD RESOLUTION: the skills matrix (1.5) queues an Odoo data migration
 * cohort at W38 and a QA & regression cohort at W41. Those are the NEXT
 * cohorts and stay queued here — the cohorts already running are earlier ones
 * (3, 4, 5), and cohort 4 is the one Vlora Ahmeti is seeded into
 * (`Competence Center · cohort 4` in `@/demo/data`). Nothing in the running
 * set repeats a subject that is still in the queue.
 */

/* ── Routes (no href is built by hand in the component) ─────────────────── */

export const POOL_HREF = '/resources/pool'
export const TRAINING_HREF = '/resources/training'
export const CAPACITY_HREF = '/resources/capacity'
export const SKILLS_HREF = '/resources/skills'

/** A run of prose in which some words are real destinations. */
export type Segment =
  | { kind: 'text'; text: string }
  | { kind: 'link'; label: string; href: string }

/* ── Chrome ─────────────────────────────────────────────────────────────── */

export const PIPELINE_CHROME = {
  moduleLabel: 'PX · Resources',
  breadcrumb: 'Growth › Talent pipeline',
  contextPill: 'COMPETENCE CENTER',
} as const

/* ── The hub the pipeline feeds ─────────────────────────────────────────── */

const KOSOVO = OFFICES.find((office) => office.code === 'KS')
if (KOSOVO === undefined) {
  throw new Error('No Kosovo office in the seed — the talent pipeline has no hub')
}

/** "Kosovo · PX-People" is the office record; the sentence says Kosovo. */
export const HUB = {
  code: KOSOVO.code,
  label: KOSOVO.name.split(' · ')[0] ?? KOSOVO.name,
  city: KOSOVO.city,
  headcount: KOSOVO.headcount,
} as const

export const HUB_BADGE = `${HUB.label} hub · ${HUB.city} · ${HUB.headcount} people`

/* ── Deck figures, transcribed once ─────────────────────────────────────── */

const DECK = {
  funnel: { applied: 340, assessed: 96, inTraining: 11, shadowing: 4, billable: 8 },
  /** Weeks from first assessment to billable work, and what it used to be. */
  timeToBillableWeeks: 11,
  timeToBillableWasWeeks: 26,
  retention12mPct: 88,
  /** FTE the Competence Center carries at the end of each year. */
  plan: [2026, 2027, 2028, 2029] as const,
  planFte: [8, 13, 20, 35] as const,
} as const

/** Weeks of structured assessment before a cohort seat is offered. */
export const ASSESSMENT_WEEKS = 2
/** Weeks paired on live client work between training and billable. */
export const SHADOW_WEEKS = 1

export const TIME_TO_BILLABLE_WEEKS = ASSESSMENT_WEEKS + TRAINING_WEEKS + SHADOW_WEEKS
if (TIME_TO_BILLABLE_WEEKS !== DECK.timeToBillableWeeks) {
  throw new Error(
    `Time to billable drifted from the deck: ${TIME_TO_BILLABLE_WEEKS} vs ${DECK.timeToBillableWeeks} weeks`
  )
}

/* ── 1. The funnel ──────────────────────────────────────────────────────── */

export interface FunnelStage {
  key: string
  label: string
  count: number
  note: string
  /** growth = the two Competence Center stages, carried in the people accent. */
  tone: 'plain' | 'growth'
  href?: string
}

const pct = (part: number, whole: number): number => Math.round((part / whole) * 100)

/* ── 2. Cohorts running ─────────────────────────────────────────────────── */

interface CohortSeed {
  number: number
  subject: string
  people: number
  /** Which week of the curriculum the cohort is in at CURRENT_WEEK. */
  weekOfCourse: number
  traineeIds?: readonly string[]
}

/**
 * The three cohorts in the Competence Center right now. Subjects are chosen
 * away from what the matrix still has queued (migration, QA) so the two
 * surfaces describe one world; the head count across them is what the funnel's
 * "In training" stage reports.
 */
const COHORT_SEEDS: readonly CohortSeed[] = [
  { number: 3, subject: 'Accounting & invoicing', people: 3, weekOfCourse: 6 },
  {
    number: 4,
    subject: 'Odoo core & delivery foundations',
    people: 4,
    weekOfCourse: 3,
    traineeIds: ['vlora-ahmeti'],
  },
  { number: 5, subject: 'Integrations & API', people: 4, weekOfCourse: 1 },
] as const

export interface TraineeLink {
  id: string
  name: string
  href: string
}

export interface CohortRow {
  key: string
  label: string
  people: number
  peopleLabel: string
  weekOfCourse: number
  progressLabel: string
  progressPct: number
  startWeekLabel: string
  billableWeekLabel: string
  trainees: readonly TraineeLink[]
}

const toCohortRow = (seed: CohortSeed): CohortRow => {
  const startWeek = CURRENT_WEEK - (seed.weekOfCourse - 1)
  const { traineeIds = [] } = seed
  return {
    key: `cohort-${seed.number}`,
    label: `Cohort ${seed.number} · ${seed.subject}`,
    people: seed.people,
    peopleLabel: `${seed.people} people`,
    weekOfCourse: seed.weekOfCourse,
    progressLabel: `week ${seed.weekOfCourse} of ${TRAINING_WEEKS}`,
    progressPct: Math.round((seed.weekOfCourse / TRAINING_WEEKS) * 100),
    startWeekLabel: `started W${startWeek}`,
    // Training runs TRAINING_WEEKS from the start week, then one week shadowing.
    billableWeekLabel: `W${startWeek + TRAINING_WEEKS + SHADOW_WEEKS}`,
    trainees: traineeIds.map((id) => {
      const person = personById(id)
      return { id, name: person.name, href: personHref(id) }
    }),
  }
}

export const RUNNING_COHORTS: readonly CohortRow[] = COHORT_SEEDS.map(toCohortRow)

const IN_TRAINING = RUNNING_COHORTS.reduce((sum, cohort) => sum + cohort.people, 0)
if (IN_TRAINING !== DECK.funnel.inTraining) {
  throw new Error(
    `Cohorts running hold ${IN_TRAINING} people but the funnel reports ${DECK.funnel.inTraining} in training`
  )
}

export const FUNNEL: readonly FunnelStage[] = [
  {
    key: 'applied',
    label: 'Applied',
    count: DECK.funnel.applied,
    note: 'over the last 12 months',
    tone: 'plain',
  },
  {
    key: 'assessed',
    label: 'Assessed',
    count: DECK.funnel.assessed,
    note: `${pct(DECK.funnel.assessed, DECK.funnel.applied)}% of applicants, ${ASSESSMENT_WEEKS} weeks each`,
    tone: 'plain',
  },
  {
    key: 'in-training',
    label: 'In training',
    count: DECK.funnel.inTraining,
    note: `${RUNNING_COHORTS.length} cohorts running`,
    tone: 'growth',
  },
  {
    key: 'shadowing',
    label: 'Shadowing',
    count: DECK.funnel.shadowing,
    note: `${SHADOW_WEEKS} week paired on live client work`,
    tone: 'growth',
  },
  {
    key: 'billable',
    label: 'Billable',
    count: DECK.funnel.billable,
    note: 'on client work today',
    tone: 'plain',
    href: POOL_HREF,
  },
] as const

export const FUNNEL_SECTION = {
  label: 'PIPELINE · LAST 12 MONTHS',
  stageLinkTitle: 'Open the resource pool',
} as const

export const COHORTS_SECTION = {
  label: 'COHORTS RUNNING',
  caption: `The ${RUNNING_COHORTS.length} Competence Center cohorts in the ${HUB.label} hub today — ${IN_TRAINING} people, each cohort ${TRAINING_WEEKS} weeks of curriculum then ${SHADOW_WEEKS} week shadowing before it bills.`,
  columns: {
    cohort: 'Cohort',
    people: 'People',
    progress: 'Progress',
    billable: 'Billable from',
  },
  inclusionPrefix: 'incl.',
  runningTag: 'In training',
} as const

/** The queue behind the running set — opened by the matrix, not by hand. */
export const COHORTS_QUEUED: readonly Segment[] = [
  {
    kind: 'text',
    text: `${QUEUED_COHORTS.map((cohort) => `${cohort.label} (${cohort.people} people) ${cohort.startWeekLabel}`).join(' · ')} — queued by the `,
  },
  { kind: 'link', label: 'skills matrix', href: SKILLS_HREF },
  { kind: 'text', text: ', not by a training budget.' },
]

/* ── 3. KPI row ─────────────────────────────────────────────────────────── */

export interface PipelineKpi {
  key: string
  label: string
  figure: string
  qualifier: readonly Segment[]
}

export const CONVERSION_PCT = pct(DECK.funnel.billable, DECK.funnel.assessed)

export const KPIS: readonly PipelineKpi[] = [
  {
    key: 'time-to-billable',
    label: 'Time to billable',
    figure: `${TIME_TO_BILLABLE_WEEKS} weeks`,
    qualifier: [
      { kind: 'text', text: `was ${DECK.timeToBillableWasWeeks} before the PX ` },
      { kind: 'link', label: 'curriculum', href: TRAINING_HREF },
    ],
  },
  {
    key: 'retention',
    label: 'Retention · 12 months',
    figure: `${DECK.retention12mPct}%`,
    qualifier: [
      { kind: 'text', text: 'of the people who reached billable work are still here a year on' },
    ],
  },
  {
    key: 'conversion',
    label: 'Assessed → billable',
    figure: `${CONVERSION_PCT}%`,
    qualifier: [
      {
        kind: 'text',
        text: `${DECK.funnel.billable} of the ${DECK.funnel.assessed} assessed reached client work — the funnel is a filter, not a promise`,
      },
    ],
  },
] as const

/* ── 4. Headcount plan ──────────────────────────────────────────────────── */

/** Share of that year's Competence Center FTE booked on client work. */
const PLAN_BILLABLE_PCT = [62, 68, 74, 80] as const

export interface PlanYear {
  key: string
  year: number
  fte: number
  fteLabel: string
  changeLabel: string
  billablePct: number
  billableLabel: string
  billableFte: number
  note: string | null
}

export const PLAN: readonly PlanYear[] = DECK.plan.map((year, index) => {
  const fte = DECK.planFte[index]
  const billablePct = PLAN_BILLABLE_PCT[index]
  if (fte === undefined || billablePct === undefined) {
    throw new Error(`The headcount plan has no figures for ${year}`)
  }
  const previous = index === 0 ? null : DECK.planFte[index - 1]
  return {
    key: String(year),
    year,
    fte,
    fteLabel: `${fte} FTE`,
    changeLabel: previous === undefined || previous === null ? 'today' : `+${fte - previous}`,
    billablePct,
    billableLabel: `${billablePct}%`,
    billableFte: Math.round((fte * billablePct) / 100),
    note: fte === HUB.headcount ? `the size the ${HUB.label} hub is today` : null,
  }
})

const FIRST_PLAN_YEAR = PLAN[0]
if (FIRST_PLAN_YEAR === undefined || FIRST_PLAN_YEAR.fte !== DECK.funnel.billable) {
  throw new Error('The plan does not start from the people the funnel says are billable today')
}

export const PLAN_SECTION = {
  label: 'HEADCOUNT PLAN',
  caption: `Competence Center FTE at the end of each year, with the share of them booked on client work. The plan starts from the ${DECK.funnel.billable} people the funnel already has billable.`,
  columns: {
    year: 'Year',
    fte: 'FTE',
    change: 'Change',
    billable: 'Billable share',
  },
  billableCellLabel: (row: PlanYear) => `${row.billableFte} of ${row.fte} on client work`,
  gating: [
    { kind: 'text', text: 'Each step is gated on the demand shown in the ' },
    { kind: 'link', label: 'capacity forecast', href: CAPACITY_HREF },
    {
      kind: 'text',
      text: ' — a year opens when the forecast shows the work and stays shut when it does not. The Competence Center is a business unit, not a training budget.',
    },
  ] satisfies readonly Segment[],
} as const

/* ── 5. Open roles — the matrix opens them, not a headcount request ─────── */

const ROLE_TITLES: Readonly<Record<string, string>> = {
  'payroll-be': 'Payroll consultant, BE',
  'cutover-infrastructure': 'Cutover & infrastructure engineer',
  'odoo-data-migration': 'Migration engineer',
}

export interface OpenRole {
  key: string
  title: string
  trigger: string
}

/** The hungriest Thin skill — training alone will not cover it in time. */
const HUNGRIEST_THIN = THIN_ROWS[0]
if (HUNGRIEST_THIN === undefined) {
  throw new Error('No thin skill on the matrix — the open-roles strip has no trigger')
}

export const OPEN_ROLES: readonly OpenRole[] = [...CRITICAL_ROWS, HUNGRIEST_THIN].map((row) => {
  const title = ROLE_TITLES[row.key]
  if (title === undefined) {
    throw new Error(`No role title authored for the triggering skill: ${row.key}`)
  }
  return {
    key: row.key,
    title,
    trigger: `${row.name}, ${row.totalHeads === 1 ? '1 holder' : `${row.totalHeads} holders`}`,
  }
})

export const OPEN_ROLES_BANNER = {
  eyebrow: 'OPEN ROLES · TRIGGERED BY THE SKILLS MATRIX',
  title: `${OPEN_ROLES.length} roles open`,
  meta: OPEN_ROLES.map((role) => `${role.title} — ${role.trigger}`).join('  ·  '),
  link: { label: 'see the matrix →', href: SKILLS_HREF },
} as const

/* ── 6. Page copy ───────────────────────────────────────────────────────── */

export const PIPELINE_COPY = {
  title: 'Talent pipeline',
  lede: `Recruiting, cohorts and time to billable for the ${HUB.label} hub — a funnel, not a promise.`,
  densityNote: [
    {
      kind: 'text',
      text: `Candidate-level rows are withheld: the funnel carries counts only, because ${DECK.funnel.applied} applications are a pipeline and naming them would commit the group to people it has not hired. The table shows the ${RUNNING_COHORTS.length} cohorts running — the queue behind them belongs to the `,
    },
    { kind: 'link', label: 'skills matrix', href: SKILLS_HREF },
    { kind: 'text', text: ' — and the plan is gated on the ' },
    { kind: 'link', label: 'capacity forecast', href: CAPACITY_HREF },
    { kind: 'text', text: ', not promised.' },
  ] satisfies readonly Segment[],
} as const
